import { motion } from 'framer-motion';
import { FileText, Video, Users } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';

const AthleteProfilePage = () => {
  return (
    <Section className="pt-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Sample Athlete Profile</h1>
          <p className="text-xl text-gray-600">
            This is an example of how your athlete profile will appear after approval.
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-64 bg-primary-100">
            <img
              src="https://images.pexels.com/photos/6111934/pexels-photo-6111934.jpeg?auto=compress&cs=tinysrgb&h=650"
              alt="Athlete cover"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative px-6 sm:px-12">
            <div className="absolute -top-20 left-1/2 transform -translate-x-1/2">
              <div className="w-40 h-40 rounded-full border-4 border-white overflow-hidden bg-white">
                <img
                  src="https://images.pexels.com/photos/4498362/pexels-photo-4498362.jpeg?auto=compress&cs=tinysrgb&h=350"
                  alt="John Doe"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="pt-24 pb-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-1">John Doe</h2>
                <p className="text-primary text-xl font-medium mb-4">Running</p>
                
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  <span className="px-3 py-1 bg-primary-50 text-primary rounded-full text-sm">
                    Intermediate
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                    Marathon
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                    Road Running
                  </span>
                </div>
                
                <div className="flex justify-center space-x-4">
                  <Button variant="primary" size="sm">
                    Support Athlete
                  </Button>
                  <Button variant="outline" size="sm">
                    Contact
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-3xl font-bold text-primary">5</span>
                  <span className="text-gray-600">Supporters</span>
                </div>
                <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-3xl font-bold text-primary">3</span>
                  <span className="text-gray-600">Competitions</span>
                </div>
                <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-3xl font-bold text-primary">$2,500</span>
                  <span className="text-gray-600">Funding Goal</span>
                </div>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Achievements</h3>
                  <p className="text-gray-700">
                    Completed several marathons with a personal best time of 3 hours and 45 minutes. 
                    Consistent training schedule of 40 miles per week for the past two years. 
                    Placed in the top 100 at the City Marathon 2024.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-3">Goals</h3>
                  <p className="text-gray-700">
                    To qualify for national championships within the next year. Looking to reduce 
                    my marathon time to under 3 hours and 30 minutes. Long-term goal of competing 
                    in international marathon events and representing my country.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-3">Motivation Statement</h3>
                  <p className="text-gray-700">
                    I am dedicated to my sport and believe with the right support, I can achieve great things. 
                    Running has been my passion since childhood, and I've worked hard to improve despite limited 
                    resources. With your support, I can access better training facilities, coaching, and 
                    competition opportunities that will help me reach my full potential.
                  </p>
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold mb-4">Documents & Media</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a
                      href="#"
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <FileText className="h-6 w-6 text-primary mr-3" />
                      <div>
                        <p className="font-medium">Training Records</p>
                        <p className="text-sm text-gray-500">PDF Document</p>
                      </div>
                    </a>
                    <a
                      href="#"
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Video className="h-6 w-6 text-primary mr-3" />
                      <div>
                        <p className="font-medium">Training Footage</p>
                        <p className="text-sm text-gray-500">Video</p>
                      </div>
                    </a>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold mb-4">Supporters</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Users className="h-6 w-6 text-primary mr-3" />
                      <div>
                        <p className="font-medium">5 supporters:</p>
                        <p className="text-gray-600">Jane Smith, Mike Johnson, Sarah Lee, Robert Chen, Emily Wilson</p>
                      </div>
                    </div>
                    <p className="text-gray-600 italic">
                      "John is an incredibly dedicated athlete with huge potential. His commitment to 
                      training and positive attitude make him a perfect candidate for support."
                      <span className="block mt-1 font-medium">— Jane Smith, Supporter</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-10 text-center">
          <Button to="/apply" variant="primary">
            Apply Now
          </Button>
        </div>
      </motion.div>
    </Section>
  );
};

export default AthleteProfilePage;